import { Wand2 } from "lucide-react";
import { useEffect, useState, type JSX } from "react";

import type { MailAddressTemplateDTO } from "../../common/api/types.js";
import { PrimaryButton } from "../../common/components/PrimaryButton.js";
import { useMailTemplate, useSaveMailTemplate } from "../adminQueries.js";

type Draft = Omit<MailAddressTemplateDTO, "domain">;

/** Wie viele Zeichen eines Namensteils; leer heißt der ganze Teil. */
const LENGTHS: Array<{ value: string; label: string }> = [
  { value: "", label: "ganz" },
  ...[1, 2, 3, 4, 5].map((count) => ({ value: String(count), label: count === 1 ? "1 Zeichen" : `${count} Zeichen` })),
];
const SEPARATORS: Array<{ value: Draft["separator"]; label: string }> = [
  { value: ".", label: "Punkt (.)" },
  { value: "-", label: "Bindestrich (-)" },
  { value: "_", label: "Unterstrich (_)" },
  { value: "", label: "ohne" },
];
const PRESETS: Array<{ label: string; template: Pick<Draft, "first" | "last" | "order" | "separator"> }> = [
  { label: "vorname.nachname", template: { first: null, last: null, order: "FIRST_LAST", separator: "." } },
  { label: "v.nachname", template: { first: 1, last: null, order: "FIRST_LAST", separator: "." } },
  { label: "vnachname", template: { first: 1, last: null, order: "FIRST_LAST", separator: "" } },
  { label: "Initialen", template: { first: 1, last: 1, order: "FIRST_LAST", separator: "" } },
];
const EXAMPLE = "Erika Mustermann";

/**
 * Administration › E-Mail: nach welchem Muster neue Personen ihre Postfachadresse
 * bekommen. Beim Anlegen und Bearbeiten wird die Adresse daraus vorgeschlagen.
 */
export function MailTemplateCard({ domains }: { domains: Array<{ id: string; name: string }> }): JSX.Element {
  const stored = useMailTemplate();
  const save = useSaveMailTemplate();
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (draft || !stored.isSuccess) return;
    const { domain: _domain, ...rest } = stored.data ?? { ...PRESETS[0]!.template, domainId: "" };
    setDraft({ ...rest, domainId: rest.domainId || domains[0]?.id || "" });
  }, [domains, draft, stored.data, stored.isSuccess]);

  if (!draft) return <section className="rounded-xl border border-line bg-surface-raised p-4 text-sm text-ink-muted">Adressvorlage wird geladen …</section>;

  // Die Domains können nach der Vorlage ankommen; dann gilt die erste.
  const current: Draft = { ...draft, domainId: draft.domainId || domains[0]?.id || "" };
  const domain = domains.find((entry) => entry.id === current.domainId)?.name ?? "";
  const preview = `${previewLocalPart(current, EXAMPLE) || "…"}@${domain}`;
  const saved = stored.data ?? null;
  const dirty = !saved || (["domainId", "first", "last", "order", "separator"] as const).some((key) => saved[key] !== current[key]);
  const patch = (next: Partial<Draft>) => setDraft({ ...current, ...next });

  return (
    <section className="rounded-xl border border-line bg-surface-raised p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-brand"><Wand2 className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-ink">Adressvorlage für Personen</h3>
          <p className="mt-0.5 text-xs text-ink-muted">Neue und bearbeitete Personen bekommen ihre Postfachadresse nach diesem Muster vorgeschlagen. Ist sie schon vergeben, wird eine Zahl angehängt.</p>
        </div>
      </div>
      <div role="radiogroup" aria-label="Muster" className="mt-3 flex flex-wrap gap-2">
        {PRESETS.map((preset) => {
          const active = preset.template.first === current.first && preset.template.last === current.last && preset.template.order === current.order && preset.template.separator === current.separator;
          return <button key={preset.label} type="button" role="radio" aria-checked={active} onClick={() => patch(preset.template)} className={active ? "rounded-full border-2 border-brand bg-surface px-3 py-1.5 text-xs font-bold text-ink" : "rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink-muted hover:text-ink"}>{preset.label}</button>;
        })}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Select label="Vorname" value={current.first === null ? "" : String(current.first)} onChange={(value) => patch({ first: value ? Number(value) : null })} options={LENGTHS} />
        <Select label="Nachname" value={current.last === null ? "" : String(current.last)} onChange={(value) => patch({ last: value ? Number(value) : null })} options={LENGTHS} />
        <Select label="Reihenfolge" value={current.order} onChange={(value) => patch({ order: value as Draft["order"] })} options={[{ value: "FIRST_LAST", label: "Vorname zuerst" }, { value: "LAST_FIRST", label: "Nachname zuerst" }]} />
        <Select label="Trenner" value={current.separator} onChange={(value) => patch({ separator: value as Draft["separator"] })} options={SEPARATORS} />
        <Select label="Domain" value={current.domainId} onChange={(value) => patch({ domainId: value })} options={domains.map((entry) => ({ value: entry.id, label: entry.name }))} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-ink-muted">Für „{EXAMPLE}“: <b className="break-all font-mono text-ink" data-testid="template-preview">{preview}</b></p>
        {stored.data ? <button type="button" onClick={() => save.mutate(null, { onSuccess: () => setDraft(null) })} className="text-sm font-semibold text-ink-muted hover:text-danger">Vorlage entfernen</button> : null}
        <PrimaryButton disabled={save.isPending || !dirty || !current.domainId} onClick={() => save.mutate(current)}>Vorlage speichern</PrimaryButton>
      </div>
      {save.error ? <p className="mt-2 text-sm text-danger">{save.error.message}</p> : null}
    </section>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }): JSX.Element {
  return (
    <label className="block text-xs font-semibold text-ink-muted">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 min-h-10 w-full rounded-lg border border-line bg-surface px-2 text-sm text-ink">
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

/**
 * Nur für die Vorschau; maßgeblich ist die Regel im Backend
 * (apps/backend/src/members/MailAddressTemplate.ts).
 */
export function previewLocalPart(template: Pick<Draft, "first" | "last" | "order" | "separator">, displayName: string): string {
  const words = displayName.split(/\s+/).map((word) => word.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "")).filter(Boolean);
  if (words.length === 0) return "";
  const firstName = words[0] ?? "";
  const lastName = words.length > 1 ? words[words.length - 1] ?? "" : "";
  const first = template.first === null ? firstName : firstName.slice(0, template.first);
  const last = template.last === null ? lastName : lastName.slice(0, template.last);
  return (template.order === "FIRST_LAST" ? [first, last] : [last, first]).filter(Boolean).join(template.separator);
}
